import { haveValue, formatPropertyAddress } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { DocIcon, OutlineShieldIcon } from "../../helper/svgHelper";

// After the landlord submits the beneficiary form for a paid insurance and
// MDP calls 4 and 5 succeeded: "Policy activated".
const InsurancePolicyActivatedPopup = ({ show, propertyData, onClose }) => {
  const { _t } = useTranslator();

  if (!show) return null;

  const insuranceData = propertyData?.insurance_data;
  // The submitted contract data; the simulation is the fallback.
  const payload =
    insuranceData?.insurancePayload || insuranceData?.simulationPayload;
  const currentLang = localStorage.getItem("i18nextLng") || "en-us";
  const address = formatPropertyAddress(propertyData, currentLang);
  const policyId = insuranceData?.policyNumber;
  const policyStart = payload?.fechaEfectoPoliza || null;
  const coverageMonths =
    payload?.garantias?.garantia?.[0]?.mesesCobImpago || 12;
  const annualPremium = haveValue(insuranceData?.payment_amount)
    ? `€${insuranceData.payment_amount} / ${_t("year")}`
    : null;
  const policyDocUrl = insuranceData?.policy_document || null;

  const insuredParties = payload?.intervinientes?.inquilinos?.inquilino || [];
  const beneficiaryText = insuredParties.length
    ? insuredParties
        .map((party) => {
          const name = [party?.nombreDocu, party?.apellido1]
            .filter(Boolean)
            .join(" ");
          return party?.numeroDocu ? `${name} · ${party.numeroDocu}` : name;
        })
        .join(", ")
    : null;

  const rows = [
    haveValue(policyId) && {
      label: _t("policyId"),
      value: `#${policyId}`,
      valueClass: "ins-activated-policy-id",
    },
    haveValue(address) && { label: _t("coveredProperty"), value: address },
    haveValue(beneficiaryText) && {
      label: _t("beneficiary"),
      value: beneficiaryText,
    },
    { label: _t("coverage"), value: `${coverageMonths} ${_t("months")}` },
    haveValue(policyStart) && { label: _t("policyStart"), value: policyStart },
  ].filter(Boolean);

  return (
    <div
      className="popup-overlay fixed inset-0 d-flex align-items-center justify-content-center z-50 wrapper-modal"
      style={{ zIndex: 9999 }}
    >
      <div className="ins-activated-card">
        <button
          type="button"
          className="ins-activated-close"
          onClick={onClose}
          aria-label={_t("close")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div className="ins-activated-icon-wrap">
          <OutlineShieldIcon />
        </div>

        <>
          <h2 className="ins-activated-heading">{_t("policyActivated")}</h2>
          <p className="ins-activated-subheading">
            {_t("policyActivatedSubheading")}
          </p>

          <div className="ins-activated-details-card">
            {rows.map((row) => (
              <div key={row.label} className="ins-activated-row">
                <span className="ins-activated-label">{row.label}</span>
                <span className={row.valueClass || "ins-activated-value-bold"}>
                  {row.value}
                </span>
              </div>
            ))}
            {haveValue(annualPremium) && (
              <>
                <div className="ins-activated-divider" />
                <div className="ins-activated-row">
                  <span className="ins-activated-label">
                    {_t("annualPremium")}
                  </span>
                  <span className="ins-activated-value-bold">
                    {annualPremium}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="ins-activated-footer">
            {haveValue(policyDocUrl) && (
              <button
                type="button"
                className="ins-activated-btn ins-activated-btn-outline"
                onClick={() => window.open(policyDocUrl, "_blank")}
              >
                <DocIcon className="ins-activated-doc-icon" />
                {_t("downloadPolicy")}
              </button>
            )}
            <button
              type="button"
              className="ins-activated-btn ins-activated-btn-outline ins-activated-btn-done"
              onClick={onClose}
            >
              {_t("done")}
            </button>
          </div>
        </>
      </div>
    </div>
  );
};

export default InsurancePolicyActivatedPopup;
